import React, {useEffect, useState, useRef} from 'react';
import {Text, Button, PhoneInput} from '@/ui-kits';
import {FileUpload, TextInput, SelectInput} from '@/ui-kits';
import {SpecialistSignupValidation} from '@/utils/schema';
import {useFormik} from 'formik';
import {specialistSignupOtpRequest, validateSpecialistInvitationRequest, verifyLicenceRequest, resetAuthMessage} from '@/services/redux/slices';
import {useDispatch, useSelector} from 'react-redux';
import {allAppSpecializations, authSessionSelector, authSuccessStatus, authFailureStatus} from '@/services/redux/selectors';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {Routes} from '@/navigation/Routes';
import {isEmpty} from 'ramda';
import {useToast} from '@/hooks';
import {ErrorCodes, getErrorMessage, getSuccessMessage, SuccessCodes} from '@/utils/getMessages';
import {resetMessage, createFormattedBlurHandler} from '@/utils/common_functions';
import {alphaNumericSpaceRegex, specialistEduccationRegex} from '@/constants/regex';
import {SIGNUP_LICENSE_FILE_KEY} from '@/constants';

export interface SpecialistFormData {
  name: string;
  email_id: string;
  phone: string;
  specialization: any;
  experience: number | null;
  designation: string;
  education: string;
  license_number: string;
  license_year: string;
  license: File | null;
  charge: number | null;
  location: number | null;
}

const userInitialValues: SpecialistFormData = {
  name: '',
  email_id: '',
  phone: '',
  specialization: null,
  experience: null,
  designation: '',
  education: '',
  license_number: '',
  license_year: '',
  license: null,
  charge: null,
  location: null,
};

export const SpecialistSignupForm: React.FC = () => {
  // ===========================
  // hook
  // ===========================
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {values, errors, touched, handleChange, setFieldTouched, setFieldError, handleBlur, setFieldValue, setValues, dirty, isValid, handleSubmit} =
    useFormik<SpecialistFormData>({
      initialValues: userInitialValues,
      validationSchema: SpecialistSignupValidation,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit,
    });

  // ===========================
  // selectors
  // ===========================
  const authSession = useSelector(authSessionSelector);
  const specialization = useSelector(allAppSpecializations);
  const authSuccess = useSelector(authSuccessStatus);
  const authFailure = useSelector(authFailureStatus);
  const {showToast} = useToast();

  // ===========================
  // states
  // ===========================
  const token = searchParams.get('token');
  const [isLicenseVerified, setIsLicenseVerified] = useState<boolean>(false);
  const licenseKeyRef = useRef<string>('');

  // ===========================
  // functions
  // ===========================
  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');
  const handleDesignationBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'designation');
  const handleEducationBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'education');

  function onSubmit(values: SpecialistFormData) {
    // Convert to FormData for multipart/form-data submission
    const formData = new FormData();

    // Add all fields to FormData
    Object.entries(values).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, value.toString());
        }
      }
    });

    // if token is present, add it to the form data
    if (token) {
      formData.append('invitation_token', token);
    }

    dispatch(specialistSignupOtpRequest(formData));
  }

  function handleLogin() {
    navigate(Routes.LOGIN);
  }

  function storeFileInLocalStorage(file: File) {
    const reader = new FileReader();

    reader.onload = () => {
      const base64String = reader.result;
      localStorage.setItem(SIGNUP_LICENSE_FILE_KEY, base64String?.toString() ?? '');
    };

    reader.readAsDataURL(file);
  }

  function getFileFromLocalStorage(filename = 'file') {
    const base64String = localStorage.getItem(SIGNUP_LICENSE_FILE_KEY);

    if (!base64String) return null;

    const [metadata, data] = base64String.split(',');
    const match = metadata.match(/data:(.*);base64/);
    if (!match) {
      return null;
    }

    const mimeType = match[1];
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    return new File([byteArray], filename, {
      type: mimeType,
    });
  }

  function handleUpload(file: File | null) {
    if (file) {
      storeFileInLocalStorage(file);
    }
    setFieldValue('license', file);
  }
  // ===========================
  // side effects
  // ===========================

  useEffect(() => {
    if (token) {
      dispatch(validateSpecialistInvitationRequest({token}));
    }
  }, [token]);

  useEffect(() => {
    if (isEmpty(authSession.formData)) {
      setValues(userInitialValues);
      return;
    }
    setValues({
      name: authSession.formData?.name || userInitialValues.name,
      email_id: authSession.email || userInitialValues.email_id,
      phone: authSession.originalPhone || userInitialValues.phone,
      designation: authSession.formData?.designation || userInitialValues.designation,
      education: authSession.formData?.education || userInitialValues.education,
      experience: authSession.formData?.experience || userInitialValues.experience,
      charge: authSession.formData?.charge || userInitialValues.charge,
      license_number: String((authSession.formData as any)?.license_number || userInitialValues.license_number || ''),
      license_year: String((authSession.formData as any)?.license_year || userInitialValues.license_year || ''),
      license: getFileFromLocalStorage(authSession.formData.license),
      location: authSession.formData?.location || userInitialValues.location,
      specialization: Number(authSession.formData?.specialization) || userInitialValues.specialization,
    });
  }, [authSession, setValues]);

  // Check if license fields are filled and valid
  const hasValidLicenseFields = Boolean(
    values.license_number &&
      values.license_year &&
      values.license_year.length === 4 &&
      values.email_id &&
      !errors.license_year &&
      !errors.license_number &&
      !errors.email_id,
  );

  // Verify licence when both license_number and license_year are filled and valid
  useEffect(() => {
    if (hasValidLicenseFields) {
      const currentLicenseKey = `${values.license_number}-${values.license_year}-${values.email_id}-${values.phone}`;

      // Reset verification if license fields changed
      if (currentLicenseKey !== licenseKeyRef.current) {
        setIsLicenseVerified(false);
        licenseKeyRef.current = currentLicenseKey;
      }

      // Debounce the API call to avoid calling on every keystroke
      const timer = setTimeout(() => {
        // Double check the key still matches before making the call
        const keyAtCallTime = `${values.license_number}-${values.license_year}-${values.email_id}-${values.phone}`;
        if (keyAtCallTime === licenseKeyRef.current) {
          // Reset verification status before making API call
          setIsLicenseVerified(false);
          dispatch(
            verifyLicenceRequest({
              license_number: values.license_number,
              license_year: values.license_year,
              email: values.email_id,
            }),
          );
        }
      }, 500);

      return () => clearTimeout(timer);
    } else {
      // Reset if fields are no longer valid
      setIsLicenseVerified(false);
      licenseKeyRef.current = '';
    }
  }, [hasValidLicenseFields, values.license_number, values.license_year, values.email_id, values.phone, dispatch]);

  // Handle verify licence API success/failure
  useEffect(() => {
    // Only process license verification responses when we have valid license fields
    if (!hasValidLicenseFields) return;

    if (authSuccess) {
      if (String(authSuccess) === 'S-10116') {
        // Verify the success is for the current license combination
        const currentLicenseKey = `${values.license_number}-${values.license_year}-${values.email_id}-${values.phone}`;
        if (currentLicenseKey === licenseKeyRef.current) {
          setIsLicenseVerified(true);
          resetMessage(() => {
            dispatch(resetAuthMessage());
          });
        }
      }
    }
    if (authFailure) {
      // Verify the failure is for the current license combination
      const currentLicenseKey = `${values.license_number}-${values.license_year}-${values.email_id}-${values.phone}`;
      if (currentLicenseKey === licenseKeyRef.current) {
        // Keep license as NOT verified when API fails (especially for E-10190)
        setIsLicenseVerified(false);
        showToast(getErrorMessage(authFailure as ErrorCodes), 'failure');
        resetMessage(() => {
          dispatch(resetAuthMessage());
        });
      }
    }
  }, [authSuccess, authFailure, hasValidLicenseFields, values.license_number, values.license_year, values.email_id, values.phone, dispatch, showToast]);

  useEffect(() => {
    if (authFailure) {
      // if the failure is due to invalid token, remove the token from the search params
      if (['E-10180', 'E-10181'].includes(authFailure as ErrorCodes)) {
        setSearchParams({}, {replace: true}); // replace the current search params with an empty object
      }
    }
  }, [authFailure]);

  return (
    <div className="flex flex-col gap-1 px-4 md:px-8 w-full">
      {/* heading */}

      <Text variant="title" className="my-4 lg:my-8 font-FigtreeBold text-ui_blue self-center inline-flex gap-1">
        Sign up for <p id="logo-text">Second Opinion</p>
      </Text>

      {/* form */}
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mg:gap-6 lg:gap-8">
          <TextInput
            label="Full Name"
            name="name"
            disabled={Boolean(token) && Boolean(authSession.formData?.name)}
            required
            placeholder="Enter your full name"
            value={values.name}
            error={errors.name}
            touched={touched.name}
            onChangeText={handleChange('name')}
            onBlur={handleNameBlur as any}
            className=" "
          />

          <TextInput
            label="Email ID"
            disabled={Boolean(token) && Boolean(authSession.email)}
            name="email_id"
            required
            placeholder="Enter your email"
            value={values.email_id}
            error={errors.email_id}
            touched={touched.email_id}
            onChangeText={handleChange('email_id')}
            onBlur={handleBlur('email_id')}
            className=" "
          />

          <PhoneInput
            label="Phone Number"
            disabled={Boolean(token) && Boolean(authSession.phone)}
            name="phone"
            country={values.location}
            required
            placeholder="Enter your phone number"
            value={values.phone}
            error={errors.phone}
            touched={touched.phone}
            onChange={(phone: string, country_id: number) => {
              setFieldValue('phone', phone, true);
              setFieldValue('location', country_id, false);
            }}
            onBlur={handleBlur('phone')}
          />

          <SelectInput
            label="Specialization"
            name="specialization"
            disabled={Boolean(token) && Boolean(authSession.formData?.specialization)}
            required
            placeholder="Ex: Cardiologist"
            value={values.specialization}
            error={errors.specialization as string}
            touched={touched.specialization as boolean}
            onChange={({id}) => setFieldValue('specialization', id)}
            onBlur={handleBlur('specialization')}
            options={specialization}
            wrapperClassName="max-w-full"
            showSearch
          />

          <TextInput
            label="Years of Experience"
            name="experience"
            required
            placeholder="Ex: 15 years"
            type="numeric"
            value={values.experience?.toString() || ''}
            error={errors.experience}
            touched={touched.experience}
            onChangeText={experience => setFieldValue('experience', experience ? Number(experience) : null)}
            onBlur={handleBlur('experience')}
            className=" "
          />
          <TextInput
            label="Designation"
            name="designation"
            required
            placeholder="Ex: Cardiologist, MD"
            value={values.designation}
            error={errors.designation}
            touched={touched.designation}
            onChangeText={handleChange('designation')}
            onBlur={handleDesignationBlur as any}
            className=" "
            preventTrailingSpace
            preventLeadingSpace
          />
        </div>

        <TextInput
          label="Education"
          name="education"
          required
          placeholder="Ex: MBBS MD"
          value={values.education}
          error={errors.education}
          touched={touched.education}
          onChangeText={handleChange('education')}
          onBlur={handleEducationBlur as any}
          className={' '}
          preventTrailingSpace
          preventLeadingSpace
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mg:gap-6 lg:gap-8">
          <TextInput
            label="License Number"
            name="license_number"
            required
            placeholder="Enter License Number"
            value={values.license_number || ''}
            error={errors.license_number}
            touched={touched.license_number}
            onChangeText={handleChange('license_number')}
            onBlur={handleBlur('license_number')}
            preventTrailingSpace
            preventLeadingSpace
            className=" "
          />

          <TextInput
            label="License Issue Year"
            name="license_year"
            required
            placeholder="Enter License Issue Year"
            type="numeric"
            value={values.license_year || ''}
            error={errors.license_year}
            touched={touched.license_year}
            onChangeText={handleChange('license_year')}
            onBlur={handleBlur('license_year')}
            preventTrailingSpace
            preventLeadingSpace
            className=" "
          />
        </div>

        <FileUpload
          label="Upload Your License"
          required
          maxSizeInMB={5}
          acceptedTypes={['pdf', 'jpg', 'png']}
          selectedFile={values.license}
          onFileSelect={handleUpload}
          error={errors.license}
          touched={touched.license}
        />
      </div>

      <Button
        disabled={!dirty || !isValid || (hasValidLicenseFields && !isLicenseVerified)}
        variant="primary"
        title="Next"
        rightIcon="arrow_circle_white_right"
        onClick={() => handleSubmit()}
        className="self-center max-w-[360px] w-full border-[1px] mt-8 mx-8"
      />

      <div className="flex flex-col gap-2 items-center mt-1">
        <Text className="text-base font-Label text-ui_blue opacity-80 self-center mt-8" variant="subtitle">
          Already have an account?
        </Text>
        <Text className="text-ui_blue font-headingSemiBold cursor-pointer text-xl" onClick={handleLogin} variant="subtitle">
          Log In
        </Text>
      </div>
    </div>
  );
};
